import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function SalesWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! 👋 Soy el asistente de AI Med Planner. ¿Eres médico o tienes un consultorio? Te puedo ayudar con información sobre cómo automatizar tu agenda y atención a pacientes.\n\n¿En qué puedo ayudarte?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '', specialty: '' });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check if we should show lead capture form after a few messages
  useEffect(() => {
    if (messages.length >= 4 && !leadCaptured && !showLeadForm) {
      // After 2 user messages, offer to capture lead
      const userMessages = messages.filter(m => m.role === 'user');
      if (userMessages.length >= 2) {
        setTimeout(() => {
          setShowLeadForm(true);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: '¡Me encanta tu interés! 🎉 Para enviarte más información personalizada y una demo, ¿me compartes tus datos?'
          }]);
        }, 1000);
      }
    }
  }, [messages, leadCaptured, showLeadForm]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/sales-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-10) // Last 10 messages for context
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Disculpa, tuve un problema. ¿Puedes intentar de nuevo?' 
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '¡Ups! Parece que hay un problema de conexión. Puedes escribirnos a contacto@aimedplanner.com' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async () => {
    if (!leadData.name || !leadData.phone) {
      alert('Por favor completa nombre y teléfono');
      return;
    }

    try {
      await fetch(`${API}/api/sales-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadData,
          source: 'website_widget',
          conversation: messages
        })
      });

      setLeadCaptured(true);
      setShowLeadForm(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `¡Perfecto ${leadData.name.split(' ')[0]}! 🙌 Te contactaremos pronto por WhatsApp para agendar una demo personalizada.\n\n¿Tienes alguna otra pregunta mientras tanto?`
      }]);
    } catch (error) {
      console.error('Error saving lead:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform z-50 animate-bounce"
          data-testid="widget-open-button"
        >
          <MessageCircle className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[32rem] bg-white rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden border border-gray-200" data-testid="sales-widget">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">AI Med Planner</h3>
                <p className="text-xs text-white/80">Asistente de Ventas</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {/* Lead Capture Form */}
            {showLeadForm && !leadCaptured && (
              <div className="bg-white rounded-xl p-4 shadow-sm border space-y-3">
                <input
                  type="text"
                  placeholder="Tu nombre"
                  value={leadData.name}
                  onChange={(e) => setLeadData({...leadData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="tel"
                  placeholder="WhatsApp (10 dígitos)"
                  value={leadData.phone}
                  onChange={(e) => setLeadData({...leadData, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="email"
                  placeholder="Email (opcional)"
                  value={leadData.email}
                  onChange={(e) => setLeadData({...leadData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={leadData.specialty}
                  onChange={(e) => setLeadData({...leadData, specialty: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Especialidad (opcional)</option>
                  <option value="oftalmologia">Oftalmología</option>
                  <option value="dermatologia">Dermatología</option>
                  <option value="cirugia_plastica">Cirugía Plástica</option>
                  <option value="odontologia">Odontología</option>
                  <option value="medicina_general">Medicina General</option>
                  <option value="ginecologia">Ginecología</option>
                  <option value="pediatria">Pediatría</option>
                  <option value="otro">Otra</option>
                </select>
                <button
                  onClick={submitLead}
                  className="w-full py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:opacity-90"
                >
                  Quiero una Demo Gratis
                </button>
                <button
                  onClick={() => setShowLeadForm(false)}
                  className="w-full py-2 text-gray-500 text-sm hover:text-gray-700"
                >
                  Ahora no, gracias
                </button>
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white px-4 py-2 rounded-2xl shadow-sm border rounded-bl-md">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta..."
                className="flex-1 px-4 py-2 border rounded-full text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
